import { useEffect, useState } from "react";
import { CONTACT } from "../data/site";

// Cinematic 4-second brand splash — first visit only (sessionStorage)
export default function SplashIntro() {
    const [shown, setShown] = useState(false);
    const [leaving, setLeaving] = useState(false);

    useEffect(() => {
        try {
            if (sessionStorage.getItem("splashSeen")) return;
        } catch (e) {
            /* ignore */
        }
        setShown(true);
        // Lock scroll during splash
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        // Start leave animation at 3.4s (0.6s fade)
        const leaveT = setTimeout(() => setLeaving(true), 3400);
        const endT = setTimeout(() => {
            setShown(false);
            document.body.style.overflow = prev;
            try {
                sessionStorage.setItem("splashSeen", "1");
            } catch (e) {}
        }, 4000);

        return () => {
            clearTimeout(leaveT);
            clearTimeout(endT);
            document.body.style.overflow = prev;
        };
    }, []);

    if (!shown) return null;

    const slogan = "Bursa Özlüce'de Profesyonel Masaj Deneyimi";

    return (
        <div
            data-testid="splash-intro"
            className={`fixed inset-0 z-[100] flex items-center justify-center bg-[#0a0a0c] overflow-hidden splash-root ${
                leaving ? "splash-leave" : ""
            }`}
            aria-hidden="true"
        >
            {/* Ambient gold glow layers */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] rounded-full bg-[#c9a24b]/18 blur-[130px] splash-glow" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-[#8a6a28]/10 blur-[180px] splash-glow-2" />
            </div>

            {/* Subtle grain */}
            <div
                className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")",
                }}
            />

            {/* Center stack */}
            <div className="relative z-10 flex flex-col items-center px-6">
                {/* Logo — expanding ring + centered logo image */}
                <div className="relative">
                    <span className="splash-ring absolute inset-0 rounded-full border border-[#c9a24b]/60" />
                    <span className="splash-ring-2 absolute inset-0 rounded-full border border-[#c9a24b]/30" />
                    <div className="splash-logo relative w-28 h-28 md:w-36 md:h-36 rounded-full overflow-hidden bg-black ring-2 ring-[#c9a24b]/50 shadow-[0_20px_60px_-20px_rgba(201,162,75,0.55)]">
                        <img
                            src={CONTACT.logoUrl}
                            alt="Cegu Masaj"
                            className="w-full h-full object-cover logo-blend"
                            loading="eager"
                        />
                    </div>
                </div>

                {/* Brand mark */}
                <div className="splash-brand mt-8 text-center">
                    <div className="text-[10.5px] uppercase tracking-[0.42em] text-[#7d766a]">
                        Est. Bursa · Özlüce
                    </div>
                    <div className="mt-3 text-4xl md:text-5xl font-bold tracking-tight">
                        <span className="gold-text">Ce&Gu</span>
                        <span className="text-[#f4efe4]"> Masaj</span>
                    </div>
                </div>

                {/* Gold divider grows */}
                <div className="splash-divider mt-6 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#c9a24b] to-transparent" />

                {/* Slogan — letter-by-letter reveal */}
                <div className="splash-slogan mt-6 max-w-lg text-center">
                    <div className="text-[13px] md:text-[15px] leading-relaxed text-[#c9c2b3] font-light">
                        {slogan}
                    </div>
                </div>

                {/* Progress bar */}
                <div className="splash-progress mt-10 w-40 md:w-56 h-[2px] rounded-full bg-white/8 overflow-hidden">
                    <div className="splash-progress-bar h-full bg-gradient-to-r from-[#f5d685] to-[#c9a24b]" />
                </div>

                <div className="splash-hint mt-4 text-[10px] uppercase tracking-[0.32em] text-[#7d766a]">
                    Yükleniyor
                </div>
            </div>

            <style>{`
                .splash-root {
                    animation: splashRootIn 0.4s ease-out both;
                }
                .splash-leave {
                    animation: splashRootOut 0.6s ease-in forwards;
                }
                @keyframes splashRootIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes splashRootOut {
                    from { opacity: 1; transform: scale(1); }
                    to { opacity: 0; transform: scale(1.04); }
                }

                .splash-logo {
                    animation: splashLogoIn 1.2s cubic-bezier(.2,.8,.2,1) both;
                }
                @keyframes splashLogoIn {
                    from { opacity: 0; transform: scale(0.6) rotate(-8deg); filter: blur(6px); }
                    60% { opacity: 1; filter: blur(0); }
                    to { opacity: 1; transform: scale(1) rotate(0deg); filter: blur(0); }
                }

                .splash-ring,
                .splash-ring-2 {
                    opacity: 0;
                }
                .splash-ring {
                    animation: splashRingA 2.2s ease-out 0.6s infinite;
                }
                .splash-ring-2 {
                    animation: splashRingA 2.2s ease-out 1.2s infinite;
                }
                @keyframes splashRingA {
                    0% { opacity: 0.6; transform: scale(1); }
                    100% { opacity: 0; transform: scale(1.8); }
                }

                .splash-brand {
                    opacity: 0;
                    animation: splashUp 0.9s cubic-bezier(.2,.9,.2,1) 0.9s forwards;
                }
                .splash-divider {
                    width: 0;
                    animation: splashDivider 0.9s cubic-bezier(.2,.9,.2,1) 1.4s forwards;
                }
                @keyframes splashDivider {
                    from { width: 0; opacity: 0; }
                    to { width: 220px; opacity: 1; }
                }
                @media (max-width: 640px) {
                    @keyframes splashDivider {
                        from { width: 0; opacity: 0; }
                        to { width: 180px; opacity: 1; }
                    }
                }
                .splash-slogan {
                    opacity: 0;
                    animation: splashUp 0.9s cubic-bezier(.2,.9,.2,1) 1.9s forwards;
                }
                .splash-progress {
                    opacity: 0;
                    animation: splashUp 0.6s ease-out 2.3s forwards;
                }
                .splash-hint {
                    opacity: 0;
                    animation: splashUp 0.6s ease-out 2.5s forwards;
                }
                @keyframes splashUp {
                    from { opacity: 0; transform: translateY(14px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .splash-progress-bar {
                    width: 0%;
                    animation: splashProgress 1.4s ease-out 2.2s forwards;
                }
                @keyframes splashProgress {
                    to { width: 100%; }
                }

                .splash-glow {
                    animation: splashGlowPulse 3.5s ease-in-out infinite;
                }
                .splash-glow-2 {
                    animation: splashGlowPulse 4.5s ease-in-out infinite;
                    animation-delay: 0.8s;
                }
                @keyframes splashGlowPulse {
                    0%, 100% { opacity: 0.6; transform: translate(-50%,-50%) scale(1); }
                    50% { opacity: 1; transform: translate(-50%,-50%) scale(1.1); }
                }
            `}</style>
        </div>
    );
}
